import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { getOptimizedImageUrl } from '../utils/imageOptimization';

const LOCALE_MAP = {
  es: 'es_ES',
  en: 'en_US',
  de: 'de_DE',
  fr: 'fr_FR',
  it: 'it_IT',
  ja: 'ja_JP',
  pt: 'pt_PT',
  'pt-BR': 'pt_BR',
  zh: 'zh_CN',
};

/**
 * SEO Head Component
 * Adds meta tags for SEO, Open Graph, and Twitter Cards
 */
const SEOHead = ({
  title,
  description,
  image,
  imageAlt,
  url,
  type = 'website',
  price,
  currency,
  availability,
  brand,
  sku,
  // Pages that are never a real destination to send search traffic to —
  // the coming-soon placeholder, 404, etc. — should never end up as
  // what Google shows for the site. Crawling is still allowed (so a
  // future noindex removal is picked up promptly); indexing isn't.
  noIndex = false,
}) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'es';
  const ogLocale = LOCALE_MAP[lang] || LOCALE_MAP[lang.split('-')[0]] || 'es_ES';
  const siteTitle = 'CimonaMía';
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  // Always the one true canonical host — never `window.location.origin`.
  // That reflected whichever host actually served the request (bare
  // domain, www, even the raw EC2 IP), so a page visited via a
  // non-canonical host declared ITSELF as canonical instead of pointing
  // back to the real one. nginx now 301s www -> bare domain (belt AND
  // suspenders — this covers any future proxy/CDN hop that might bypass
  // that redirect). Same hardcoded-fallback convention already used
  // throughout the backend (sitemap.py, agent.py's SITE_URL).
  const siteUrl = 'https://cimonamia.com';
  // Also never window.location.href as a fallback: that's the full raw
  // URL including query strings/hash (?utm_source=..., #section, ...),
  // which would bake tracking params straight into the canonical tag —
  // exactly the kind of self-inflicted duplicate-content signal this
  // fix is trying to eliminate. Fall back to the clean pathname instead.
  const fullUrl = `${siteUrl}${url || window.location.pathname}`;
  
  // Get optimized image URL (prefer large for social sharing)
  const imageUrl = image ? 
    (typeof image === 'string' ? image : getOptimizedImageUrl(image, 'large', false)) 
    : null;
  const fullImageUrl = imageUrl?.startsWith('http') ? imageUrl : `${siteUrl}${imageUrl}`;

  // index.html ships a static description/robots/og:*/twitter:* baseline
  // for clients that never run this JS at all (see the comment on those
  // tags) — but react-helmet-async doesn't adopt/replace tags that were
  // already in the DOM before it mounted, it only adds its own alongside
  // them. Left alone, every JS-rendering visitor (real browsers, Googlebot,
  // any renderer-based tool) would end up with BOTH the generic static
  // version and this page's real one at once. Runs once, the very first
  // time any SEOHead mounts — by then Helmet's own tags are already in the
  // DOM, so removing the static ones just leaves the correct set behind.
  useEffect(() => {
    document.head.querySelectorAll('[data-static-seo="true"]').forEach((el) => el.remove());
  }, []);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {/* NOTE: react-helmet-async doesn't reliably flatten a <>...</>
          Fragment wrapping multiple <meta> children — a Fragment-wrapped
          group here silently drops every tag inside it, not just some.
          Always render each tag as its own direct child of <Helmet> and
          vary only the `content` string, never swap element groups. */}
      <meta name="robots" content={noIndex ? "noindex, follow" : "index, follow"} />
      <meta
        name="googlebot"
        content={noIndex ? "noindex, follow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"}
      />
      <meta
        name="bingbot"
        content={noIndex ? "noindex, follow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"}
      />
      <link rel="canonical" href={fullUrl} />
      
      {/* Language and Locale */}
      <html lang={lang} />
      <meta property="og:locale" content={ogLocale} />
      {ogLocale !== 'es_ES' && <meta property="og:locale:alternate" content="es_ES" />}
      
      {/* Theme Color for Mobile Browsers */}
      <meta name="theme-color" content="#be1f1a" />
      <meta name="msapplication-TileColor" content="#be1f1a" />
      
      {/* Open Graph Meta Tags (Facebook, LinkedIn) */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {imageUrl && <meta property="og:image" content={fullImageUrl} />}
      {imageUrl && <meta property="og:image:secure_url" content={fullImageUrl} />}
      {imageUrl && imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      {imageUrl && <meta property="og:image:width" content="1400" />}
      {imageUrl && <meta property="og:image:height" content="1400" />}
      {imageUrl && <meta property="og:image:type" content="image/jpeg" />}
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content={siteTitle} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {imageUrl && <meta name="twitter:image" content={fullImageUrl} />}
      {imageUrl && imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      {/* Product-specific Meta Tags (Schema.org) — each its own direct
          Helmet child (see the robots/googlebot/bingbot note above); a
          Fragment wrapping this whole group was silently dropping all
          five tags on every product page, not just the missing ones. */}
      {type === 'product' && price && <meta property="product:price:amount" content={String(price)} />}
      {type === 'product' && price && <meta property="product:price:currency" content={String(currency)} />}
      {type === 'product' && price && availability && <meta property="product:availability" content={String(availability)} />}
      {type === 'product' && price && brand && <meta property="product:brand" content={String(brand)} />}
      {type === 'product' && price && sku && <meta property="product:retailer_item_id" content={String(sku)} />}
      
      {/* JSON-LD Structured Data. A present-but-empty string (the old
          `description || ''` / `sku || ''` pattern) fails Google's Merchant
          listings validator exactly like an absent field does — omit the
          key entirely instead when there's no real value, rather than
          publish an empty one. */}
      {type === 'product' && price && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": title,
            "image": imageUrl ? [fullImageUrl] : [],
            ...(description ? { description } : {}),
            ...(sku ? { sku } : {}),
            "brand": brand ? {
              "@type": "Brand",
              "name": brand
            } : undefined,
            "offers": {
              "@type": "Offer",
              "url": fullUrl,
              "priceCurrency": currency || 'USD',
              "price": String(price),
              "availability": availability === 'in stock'
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
              "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
              // Real published policy (/page/shipping-returns): exchanges
              // only (not cash refunds), within 3 business days of
              // delivery. No site-wide shippingDetails here — the store's
              // shipping cost is a dynamic per-carrier rate (Envía.com),
              // not a fixed number, and the one per-product `shippingInfo`
              // value that exists in the catalog is a one-off test entry,
              // not real data — publishing either would misstate the
              // actual price to shoppers.
              "hasMerchantReturnPolicy": {
                "@type": "MerchantReturnPolicy",
                "applicableCountry": "CO",
                "returnPolicyCategory": "https://schema.org/MerchandiseReturnFiniteReturnWindow",
                "merchantReturnDays": 3,
                "returnMethod": "https://schema.org/ReturnByMail",
                "refundType": "https://schema.org/ExchangeRefund"
              }
            }
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
